import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight, Plus } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { TextInput } from "../../../ui/components/Form";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  fetchCommandDefinitions,
  type CommandDefinition,
  type CommandIotDomain,
} from "../../lib/api/commands";
import { CommandDangerPill } from "../commands/CommandPills";
import * as s from "../commands/commands.css";
import * as cs from "./CommandCatalog.css";

const DOMAINS: Array<CommandIotDomain | "all"> = ["all", "sanctuary", "powersave"];

function groupLabel(t: (key: string) => string, d: CommandDefinition): string {
  const line = t(`pro.commands.domain.${d.iotDomain}`);
  return d.componentScope ? `${line} · ${d.componentScope.toUpperCase()}` : `${line} · ${d.category}`;
}

export function CommandCatalogListPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");

  const [definitions, setDefinitions] = React.useState<CommandDefinition[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [domain, setDomain] = React.useState<CommandIotDomain | "all">("all");
  const [query, setQuery] = React.useState("");

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchCommandDefinitions(token)
      .then(setDefinitions)
      .catch((err) => setError(err?.message || t("pro.commands.catalog.error.load")))
      .finally(() => setLoading(false));
  }, [token, t]);

  const groups = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<string, CommandDefinition[]>();
    definitions
      .filter((d) => domain === "all" || d.iotDomain === domain)
      .filter(
        (d) =>
          !q ||
          d.displayName.toLowerCase().includes(q) ||
          d.key.toLowerCase().includes(q) ||
          d.category.toLowerCase().includes(q),
      )
      .forEach((d) => {
        const label = groupLabel(t, d);
        map.set(label, [...(map.get(label) ?? []), d]);
      });
    return [...map.entries()];
  }, [definitions, domain, query, t]);

  const open = (definitionId: string) =>
    navigate({ to: "/system/command-catalog/$definitionId", params: { definitionId } });

  return (
    <div className={cs.page}>
      <div className={cs.header}>
        <div>
          <Text level="h1" weight="bold">
            {t("pro.commands.catalog.title")}
          </Text>
          <span className={s.muted}>{t("pro.commands.catalog.subtitle")}</span>
        </div>
        <Button type="button" color="primary" iconLeft={<Plus size={14} />} onClick={() => open("new")}>
          {t("pro.commands.catalog.new")}
        </Button>
      </div>

      <div className={s.toolbar}>
        {DOMAINS.map((d) => (
          <button
            key={d}
            type="button"
            className={`${s.chip}${domain === d ? ` ${s.chipActive}` : ""}`}
            onClick={() => setDomain(d)}
          >
            {d === "all" ? t("pro.commands.section.all") : t(`pro.commands.domain.${d}`)}
          </button>
        ))}
        <div className={s.search}>
          <TextInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("pro.commands.catalog.search")}
            aria-label={t("pro.commands.catalog.search")}
          />
        </div>
      </div>

      {error && <Callout variant="error">{error}</Callout>}
      {loading && <AuthTransitionScreen variant="compact" message={t("shared.common.loading")} />}
      {!loading && !error && groups.length === 0 && (
        <span className={s.muted}>{t("pro.commands.catalog.empty")}</span>
      )}

      {groups.map(([label, rows]) => (
        <div key={label}>
          <div className={s.groupTitle}>{label}</div>
          <div className={s.activityList}>
            {rows.map((d) => (
              <button key={d.id} type="button" className={cs.row} onClick={() => open(d.id)}>
                <div style={{ minWidth: 0 }}>
                  <div className={s.commandName}>{d.displayName}</div>
                  <div className={s.subtle}>
                    {d.key} · {t(`pro.commands.transport.${d.transport}`)} ·{" "}
                    {t("pro.commands.catalog.timeoutSeconds", { seconds: Math.round(d.timeoutMs / 1000) })}
                  </div>
                </div>
                <div className={s.inlineRow}>
                  {!d.enabled && <span className={s.pill.neutral}>{t("pro.commands.catalog.disabled")}</span>}
                  <CommandDangerPill level={d.dangerLevel} />
                  <ChevronRight size={16} className={cs.chevron} />
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
